// Grid-based email signature builder types
import { SignatureBlock } from '../blocks/types';

export interface GridCell {
  id: string;
  type: 'cell';
  content: SignatureBlock | null;
  children: GridCell[] | null;
  splitDirection: 'horizontal' | 'vertical' | null;
  widths: string[] | null; // Widths for child cells when split, e.g., ['50%', '50%']
  depth: number;
}

export interface SignatureGridData {
  version: 2;
  rootCells: GridCell[];
  globalStyles: {
    fontFamily: string;
    backgroundColor: string;
    padding: number;
  };
}

export interface SplitPattern {
  id: string;
  label: string;
  widths: string[];
  preview: number[]; // For visual representation (relative widths)
}

export const MAX_NESTING_DEPTH = 5;

// Split patterns for horizontal splits (columns)
export const horizontalPatterns: SplitPattern[] = [
  { id: 'h-50-50', label: 'Equal halves', widths: ['50%', '50%'], preview: [1, 1] },
  { id: 'h-33-33-33', label: 'Three equal', widths: ['33.33%', '33.33%', '33.34%'], preview: [1, 1, 1] },
  { id: 'h-30-70', label: 'Sidebar left', widths: ['30%', '70%'], preview: [3, 7] },
  { id: 'h-70-30', label: 'Sidebar right', widths: ['70%', '30%'], preview: [7, 3] },
  { id: 'h-25-50-25', label: 'Center focus', widths: ['25%', '50%', '25%'], preview: [1, 2, 1] },
];

// Split patterns for vertical splits (rows)
export const verticalPatterns: SplitPattern[] = [
  { id: 'v-50-50', label: 'Equal halves', widths: ['auto', 'auto'], preview: [1, 1] },
  { id: 'v-33-33-33', label: 'Three equal', widths: ['auto', 'auto', 'auto'], preview: [1, 1, 1] },
  { id: 'v-30-70', label: 'Small top', widths: ['auto', 'auto'], preview: [3, 7] },
  { id: 'v-70-30', label: 'Small bottom', widths: ['auto', 'auto'], preview: [7, 3] },
];

// Helper function to create a unique ID
export const createCellId = (): string => {
  return Math.random().toString(36).substring(2, 11);
};

// Create a new empty cell
export const createCell = (depth: number = 0): GridCell => ({
  id: createCellId(),
  type: 'cell',
  content: null,
  children: null,
  splitDirection: null,
  widths: null,
  depth,
});

// Check if a cell can be split (not at max depth)
export const canSplit = (cell: GridCell): boolean => {
  return cell.depth < MAX_NESTING_DEPTH;
};

// Split a cell into multiple children
export const splitCell = (
  cell: GridCell,
  direction: 'horizontal' | 'vertical',
  widths: string[]
): GridCell => {
  const newChildren: GridCell[] = widths.map((_, index) => {
    const childCell = createCell(cell.depth + 1);
    // Move existing content to first child
    if (index === 0 && cell.content) {
      childCell.content = cell.content;
    }
    return childCell;
  });

  return {
    ...cell,
    content: null, // Clear content since it's moved to first child
    children: newChildren,
    splitDirection: direction,
    widths,
  };
};

// Merge cell children back into single cell (unsplit)
export const unsplitCell = (cell: GridCell): GridCell => {
  if (!cell.children || cell.children.length === 0) return cell;

  // Find first child with content
  const firstContentChild = cell.children.find(c => c.content !== null);
  
  return {
    ...cell,
    content: firstContentChild?.content || null,
    children: null,
    splitDirection: null,
    widths: null,
  };
};

// Find a cell by ID in the grid
export const findCellById = (cells: GridCell[], id: string): GridCell | null => {
  for (const cell of cells) {
    if (cell.id === id) return cell;
    if (cell.children) {
      const found = findCellById(cell.children, id);
      if (found) return found;
    }
  }
  return null;
};

// Update a cell in the grid
export const updateCellInGrid = (
  cells: GridCell[],
  cellId: string,
  updater: (cell: GridCell) => GridCell
): GridCell[] => {
  return cells.map(cell => {
    if (cell.id === cellId) {
      return updater(cell);
    }
    if (cell.children) {
      return {
        ...cell,
        children: updateCellInGrid(cell.children, cellId, updater),
      };
    }
    return cell;
  });
};

// Delete a cell from the grid
export const deleteCellFromGrid = (cells: GridCell[], cellId: string): GridCell[] => {
  return cells.filter(cell => cell.id !== cellId).map(cell => {
    if (cell.children) {
      const newChildren = deleteCellFromGrid(cell.children, cellId);
      // If only one child remains after deletion, unsplit
      if (newChildren.length === 1) {
        return {
          ...cell,
          content: newChildren[0].content,
          children: newChildren[0].children,
          splitDirection: newChildren[0].splitDirection,
          widths: newChildren[0].widths,
          // Don't change depth - this cell keeps its depth
        };
      }
      // Update widths array to match remaining children
      if (newChildren.length < (cell.children?.length || 0)) {
        const equalWidth = `${100 / newChildren.length}%`;
        return {
          ...cell,
          children: newChildren,
          widths: newChildren.map(() => equalWidth),
        };
      }
      return { ...cell, children: newChildren };
    }
    return cell;
  });
};

// Count total content blocks in a cell and its children
export const countContentInCell = (cell: GridCell): number => {
  let count = cell.content ? 1 : 0;
  if (cell.children) {
    count += cell.children.reduce((sum, child) => sum + countContentInCell(child), 0);
  }
  return count;
};

// Create default grid data
export const createDefaultGridData = (): SignatureGridData => ({
  version: 2,
  rootCells: [createCell(0)],
  globalStyles: {
    fontFamily: 'Arial, sans-serif',
    backgroundColor: '#ffffff',
    padding: 0,
  },
});

// Migration helper: convert old SignatureData to SignatureGridData
export const migrateToGridData = (oldData: { version: number; blocks: SignatureBlock[] }): SignatureGridData => {
  const rootCells: GridCell[] = oldData.blocks.map(block => ({
    id: createCellId(),
    type: 'cell' as const,
    content: block,
    children: null,
    splitDirection: null,
    widths: null,
    depth: 0,
  }));

  return {
    version: 2,
    rootCells: rootCells.length > 0 ? rootCells : [createCell(0)],
    globalStyles: {
      fontFamily: 'Arial, sans-serif',
      backgroundColor: '#ffffff',
      padding: 0,
    },
  };
};
